// MyLibrary — the buyer's editorial "magazine rack".
// Three sections: In Progress · Completed · What's Next (recommendations to buy).
// Designed to feel like flipping through a publication catalogue, not a generic file list.
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { getWithRetry } from "../lib/apiResilient";
import { toast } from "sonner";
import { usePortalAuth } from "../portal/PortalAuth";
import { Reveal, MaskReveal } from "../components/Reveal";
import {
  ArrowUpRight, BookOpen, CheckCircle2, Clock, LogOut,
  Sparkles, ShoppingBag,
} from "lucide-react";
import WelcomeOverlay from "../components/WelcomeOverlay";

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Atoms ──────────────────────────────────────────────────────────────
function ProgressBar({ value }) {
  const pct = Math.max(0, Math.min(100, Math.round(value || 0)));
  return (
    <div className="h-[2px] w-full bg-ink/10 overflow-hidden">
      <div
        className="h-full bg-ink transition-[width] duration-700 ease-out"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function EditionCover({ item, badge }) {
  return (
    <div className="relative aspect-[3/4] bg-ink overflow-hidden">
      <img
        src={`${API}${item.cover_url}`}
        alt={item.title}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
      />
      {/* subtle gradient & corner edition number */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-ink/85" />
      <div className="absolute top-3 left-3 font-display text-cream/85 text-lg tracking-[0.18em]">
        № {item.edition_no}
      </div>
      {badge && (
        <div className="absolute top-3 right-3 overline text-[9px] bg-cream/95 text-ink px-2 py-1 tracking-[0.22em]">
          {badge}
        </div>
      )}
      <div className="absolute bottom-3 left-3 right-3">
        <div className="overline text-cream/65 text-[9px] mb-1">{item.tier_label}</div>
        <div className="font-serif text-cream text-[15px] leading-snug line-clamp-2">{item.title}</div>
      </div>
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────
export default function MyLibrary() {
  const { user, logout } = usePortalAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [recs, setRecs] = useState([]);
  const [error, setError] = useState(null);

  // Preload the PDF.js worker as soon as a buyer opens their shelf — by the
  // time they click an edition the 1.3MB worker is already in the browser
  // cache, cutting first-render time on the reader from ~10s to ~2s.
  useEffect(() => {
    if (document.head.querySelector('link[data-pdf-worker-preload]')) return;
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "script";
    link.href = "/pdfjs/pdf.worker.min.js";
    link.type = "application/javascript";
    link.setAttribute("data-pdf-worker-preload", "1");
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [lib, rec] = await Promise.all([
          getWithRetry(`${API}/api/library/me`, { withCredentials: true }),
          getWithRetry(`${API}/api/library/recommendations?limit=6`, { withCredentials: true })
            .catch(() => ({ data: { items: [] } })),
        ]);
        if (cancelled) return;
        setItems(lib.data.items || []);
        setRecs(rec.data.items || []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Couldn't load your library.");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const { inProgress, completed, fresh } = useMemo(() => {
    const list = items || [];
    const inP = []; const done = []; const fr = [];
    for (const it of list) {
      if (it.completed) done.push(it);
      else if (it.last_page && it.last_page > 1) inP.push(it);
      else fr.push(it);
    }
    // Most recent first inside each bucket
    const byDate = (a, b) => (b.purchased_at || "").localeCompare(a.purchased_at || "");
    inP.sort(byDate); done.sort(byDate); fr.sort(byDate);
    return { inProgress: inP, completed: done, fresh: fr };
  }, [items]);

  const handleLogout = async () => {
    await logout();
    navigate("/library/login");
  };

  if (error) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="overline text-ink/55 mb-4">Library · Issue</div>
          <p className="font-serif italic text-xl text-ink/80">{error}</p>
          <Link to="/library/login" className="inline-block mt-6 overline text-ink border-b border-ink/40 pb-1">Sign in again</Link>
        </div>
      </div>
    );
  }

  if (!items) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="overline text-ink/55 animate-pulse">Pulling editions from the rack…</div>
      </div>
    );
  }

  const total = items.length;
  const doneCount = completed.length;
  const completionPct = total ? Math.round((doneCount / total) * 100) : 0;

  return (
    <div className="bg-cream min-h-screen text-ink pt-28 md:pt-36 pb-32" data-testid="my-library-page">
      {/* Welcome overlay — only fires on `?welcome=1&to=…` (post-checkout) */}
      <WelcomeOverlay />

      {/* Masthead */}
      <section className="container-edge">
        <div className="grid md:grid-cols-12 gap-y-8 gap-x-12 items-end mb-14 md:mb-20">
          <div className="md:col-span-8">
            <div className="overline text-ink/55">M Media Studio · {(user?.name || user?.email || "").split(" ")[0] || "Reader"}</div>
            <MaskReveal delay={0.05}>
              <h1 className="font-display uppercase text-5xl sm:text-6xl md:text-[8vw] leading-[0.85] mt-3">
                Your <span className="serif-italic font-serif normal-case">shelf</span>.
              </h1>
            </MaskReveal>
            <p className="font-serif italic text-ink/70 text-lg md:text-xl mt-5 max-w-2xl leading-snug">
              Pick up where you left off. Or start a new edition — your place is saved on every one.
            </p>
          </div>
          <Reveal delay={0.12} className="md:col-span-4 md:text-right">
            <div className="overline text-ink/55 mb-2">Reading score</div>
            <div className="font-display text-5xl md:text-6xl leading-none mb-2 tabular-nums">{doneCount}<span className="text-ink/30"> / </span>{total}</div>
            <div className="text-[12px] text-ink/55 font-serif italic mb-3">editions completed</div>
            <ProgressBar value={completionPct} />
            <button
              onClick={handleLogout}
              data-testid="library-logout"
              className="mt-6 inline-flex items-center gap-2 overline text-ink/55 hover:text-ink transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" />
              <span>Sign out</span>
            </button>
          </Reveal>
        </div>

        {/* Continue Reading — pride of place */}
        {inProgress.length > 0 && (
          <div className="mb-20">
            <SectionHeader
              kicker="Continue reading"
              title={<>Pick up the <em className="serif-italic font-serif">thread</em>.</>}
              count={inProgress.length}
            />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-10">
              {inProgress.map((it, i) => (
                <Reveal key={it.slug} delay={i * 0.04}>
                  <ContinueCard item={it} />
                </Reveal>
              ))}
            </div>
          </div>
        )}

        {/* On the shelf — not yet opened */}
        {fresh.length > 0 && (
          <div className="mb-20">
            <SectionHeader
              kicker="On the shelf"
              title={<>Yet to be <em className="serif-italic font-serif">opened</em>.</>}
              count={fresh.length}
            />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-10">
              {fresh.map((it, i) => (
                <Reveal key={it.slug} delay={i * 0.04}>
                  <ShelfCard item={it} />
                </Reveal>
              ))}
            </div>
          </div>
        )}

        {/* Completed — trophy case */}
        {completed.length > 0 && (
          <div className="mb-20">
            <SectionHeader
              kicker="Finished"
              title={<>Already on the <em className="serif-italic font-serif">record</em>.</>}
              count={completed.length}
            />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-10">
              {completed.map((it, i) => (
                <Reveal key={it.slug} delay={i * 0.04}>
                  <CompletedCard item={it} />
                </Reveal>
              ))}
            </div>
          </div>
        )}

        {/* What's next — buy more editions, blended into the experience */}
        {recs.length > 0 && (
          <div className="mt-24 md:mt-32">
            <SectionHeader
              kicker="What's next"
              title={<>Hand-picked for <em className="serif-italic font-serif">you</em>.</>}
              count={recs.length}
              accent
            />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-x-5 md:gap-x-8 gap-y-12">
              {recs.map((r, i) => (
                <Reveal key={r.slug} delay={i * 0.06}>
                  <RecCard item={r} />
                </Reveal>
              ))}
            </div>
            <div className="mt-12 flex justify-center">
              <Link
                to="/studio"
                className="inline-flex items-center gap-3 border border-ink/30 px-7 py-4 overline tracking-[0.32em] hover:bg-ink hover:text-cream transition-colors"
                data-testid="library-browse-all"
              >
                <ShoppingBag className="h-4 w-4" />
                <span>Browse all 45 editions</span>
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        )}

        {/* Empty shelf state — should rarely fire, but feels intentional if it does */}
        {total === 0 && (
          <div className="text-center py-24">
            <BookOpen className="h-10 w-10 text-ink/30 mx-auto mb-6" />
            <h2 className="font-display uppercase text-4xl md:text-5xl mb-4">Your shelf is empty.</h2>
            <p className="font-serif italic text-ink/65 max-w-md mx-auto mb-8">
              Start with a Listicle for $9, or grab the Pick-5 Bundle for $129. Every edition you buy lands here automatically.
            </p>
            <Link to="/studio" className="inline-flex items-center gap-3 bg-ink text-cream px-8 py-4 overline tracking-[0.3em] hover:bg-ink/90 transition-colors">
              Open the Studio <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}

// ─── Section header ─────────────────────────────────────────────────────
function SectionHeader({ kicker, title, count, accent }) {
  return (
    <div className="flex items-end justify-between flex-wrap gap-y-3 mb-8 md:mb-10 border-b border-ink/15 pb-4">
      <div>
        <div className={`overline ${accent ? "text-ink" : "text-ink/55"} mb-2`}>
          {accent && <Sparkles className="inline-block h-3 w-3 mr-2 -mt-1" />}{kicker} {count != null && <span className="opacity-50">· {count}</span>}
        </div>
        <h2 className="font-display uppercase text-3xl md:text-4xl leading-[0.95]">{title}</h2>
      </div>
    </div>
  );
}

// ─── Cards ──────────────────────────────────────────────────────────────
function ContinueCard({ item }) {
  const pct = item.total_pages ? Math.round((item.last_page / item.total_pages) * 100) : null;
  return (
    <Link
      to={`/library/${item.slug}`}
      className="group block"
      data-testid={`continue-card-${item.slug}`}
    >
      <EditionCover item={item} badge={pct ? `${pct}% IN` : "READING"} />
      <div className="mt-3">
        <div className="flex items-center gap-2 text-[11px] text-ink/55 overline">
          <Clock className="h-3 w-3" /> Resumes at p.{item.last_page || 1}
        </div>
        {pct != null && <div className="mt-2"><ProgressBar value={pct} /></div>}
      </div>
    </Link>
  );
}

function ShelfCard({ item }) {
  return (
    <Link
      to={`/library/${item.slug}`}
      className="group block"
      data-testid={`shelf-card-${item.slug}`}
    >
      <EditionCover item={item} badge={item.is_free ? "FREE" : "NEW"} />
      <div className="mt-3 flex items-center gap-2 text-[11px] overline text-ink/55">
        <BookOpen className="h-3 w-3" /> Start reading
      </div>
    </Link>
  );
}

function CompletedCard({ item }) {
  return (
    <Link
      to={`/library/${item.slug}`}
      className="group block"
      data-testid={`completed-card-${item.slug}`}
    >
      <div className="relative">
        <EditionCover item={item} badge="✓ DONE" />
        {/* "completed" overlay tint */}
        <div className="absolute inset-0 bg-cream/15 mix-blend-overlay pointer-events-none" />
      </div>
      <div className="mt-3 flex items-center gap-2 text-[11px] overline text-ink/55">
        <CheckCircle2 className="h-3 w-3" /> Read it again
      </div>
    </Link>
  );
}

function RecCard({ item }) {
  return (
    <Link
      to={`/studio/${item.slug}`}
      className="group block"
      data-testid={`rec-card-${item.slug}`}
    >
      <div className="relative aspect-[3/4] bg-ink overflow-hidden">
        <img
          src={`${API}${item.cover_url}`}
          alt={item.title}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-ink/90" />
        <div className="absolute top-3 left-3 font-display text-cream/85 text-lg tracking-[0.18em]">
          № {item.edition_no}
        </div>
        <div className="absolute top-3 right-3 overline text-[9px] bg-cream text-ink px-2 py-1 tracking-[0.22em]">
          ${item.price}
        </div>
        <div className="absolute bottom-4 left-4 right-4">
          <div className="overline text-cream/70 text-[9px] mb-2">{item.tier_label} · {item.category_label}</div>
          <div className="font-serif text-cream text-[17px] leading-tight line-clamp-2 mb-2">{item.title}</div>
          {item.blurb && <div className="text-cream/75 text-[12px] font-serif italic line-clamp-2 leading-snug">{item.blurb}</div>}
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between text-[11px] overline">
        <span className="text-ink/65">Add to shelf</span>
        <ArrowUpRight className="h-3.5 w-3.5 text-ink/65 group-hover:translate-x-0.5 transition-transform" />
      </div>
    </Link>
  );
}
